import React from 'react';
import { useTranslation } from 'react-i18next';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { SectionTitle, BlockHeading, CodeBlock, InfoCard, NumberedList, Panel, useApiDocs } from '../../../components/apidocs';
import { buildSignatureSamples } from '../data/codeSamples';

/** ปุ่มสลับเวอร์ชัน V2/V1 ด้านบนของ section */
const VersionToggle: React.FC = () => {
  const { apiVersion, setApiVersion } = useApiDocs();
  return (
    <div className="flex gap-2 mb-6">
      <button
        className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
          apiVersion === 'v2'
            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
        }`}
        onClick={() => setApiVersion('v2')}
      >
        V2 - Signature (Recommended)
      </button>
      <button
        className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
          apiVersion === 'v1'
            ? 'bg-gray-600 text-white shadow-lg shadow-gray-600/20'
            : 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'
        }`}
        onClick={() => setApiVersion('v1')}
      >
        V1 - API Secret (Legacy)
      </button>
    </div>
  );
};

/** V2 — HMAC signature */
const SignatureAuthDocs: React.FC = () => {
  const { t } = useTranslation();
  const { apiKey, baseUrlV2 } = useApiDocs();

  const signatureSteps = [
    <>สร้าง payload string: <code className="bg-white px-2 py-0.5 rounded text-xs border border-gray-200 font-mono text-indigo-600">timestamp + method + path + body</code></>,
    <>เข้ารหัสด้วย <code className="bg-white px-2 py-0.5 rounded text-xs border border-gray-200 font-mono text-indigo-600">HMAC-SHA256(payload, secret_key)</code></>,
    <>แปลงเป็น hex string แล้วใส่ใน header <code className="bg-white px-2 py-0.5 rounded text-xs border border-gray-200 font-mono text-indigo-600">X-Signature</code></>,
  ];

  return (
    <>
      <InfoCard type="success" title="V2 Recommended">
        <span>ใช้ HMAC-SHA256 Signature เพื่อความปลอดภัยสูงสุด Secret Key จะไม่ถูกส่งผ่าน network</span>
      </InfoCard>

      <Panel>
        <BlockHeading color="text-gray-800 dark:text-slate-100">Base URL</BlockHeading>
        <CodeBlock code={baseUrlV2} label="endpoint" id="base-url-v2" />
      </Panel>

      <Panel>
        <BlockHeading color="text-gray-800 dark:text-slate-100">Required Headers</BlockHeading>
        <CodeBlock
          code={`X-API-Key: ${apiKey}\nX-Timestamp: <unix_timestamp_seconds>\nX-Signature: <hmac_sha256_signature>\nContent-Type: application/json`}
          label="headers"
          id="v2-headers"
        />
      </Panel>

      <Panel>
        <BlockHeading color="text-gray-800 dark:text-slate-100" margin="mb-4">How to Generate Signature</BlockHeading>
        <NumberedList items={signatureSteps} variant="indigo" className="mb-5" />

        <div className="space-y-4">
          {buildSignatureSamples(apiKey).map(sample => (
            <CodeBlock key={sample.id} label={sample.label} id={sample.id} code={sample.code} />
          ))}
        </div>
      </Panel>

      <InfoCard type="info" title="Replay Protection">
        Timestamp ต้องไม่ต่างจากเวลา server เกิน 5 นาที เพื่อป้องกัน replay attack
      </InfoCard>

      <InfoCard type="warning" title={t('apiDocs.important')}>
        Secret Key ใช้สร้าง signature ฝั่ง server ของคุณเท่านั้น ห้ามส่งผ่าน header หรือเปิดเผยใน frontend/client-side code
      </InfoCard>
    </>
  );
};

/** V1 — API key + secret (legacy) */
const ApiSecretAuthDocs: React.FC = () => {
  const { t } = useTranslation();
  const { apiKey } = useApiDocs();

  return (
    <>
      <InfoCard type="warning" title="V1 Legacy">
        V1 ยังใช้งานได้ปกติ แต่แนะนำให้ migrate ไป V2 เพื่อความปลอดภัยที่ดีกว่า (Secret Key จะไม่ถูกส่งผ่าน network)
      </InfoCard>

      <Panel>
        <BlockHeading color="text-gray-800 dark:text-slate-100" margin="mb-2">API Keys</BlockHeading>
        <p className="text-gray-500 dark:text-slate-400 mb-4 text-sm">{t('apiDocs.authHeadersDesc')}</p>
        <CodeBlock
          code={`X-API-Key: ${apiKey}\nX-API-Secret: sk_your_api_secret\nContent-Type: application/json`}
          label="headers"
          id="v1-headers"
        />
      </Panel>

      <InfoCard type="warning" title={t('apiDocs.important')}>
        {t('apiDocs.doNotShareSecret')}
      </InfoCard>
    </>
  );
};

const AuthenticationSection: React.FC = () => {
  const { apiVersion } = useApiDocs();
  return (
    <div>
      <SectionTitle icon={faKey} iconColor="bg-gradient-to-br from-amber-400 to-yellow-500">
        Authentication
      </SectionTitle>

      <VersionToggle />

      {apiVersion === 'v2' ? <SignatureAuthDocs /> : <ApiSecretAuthDocs />}
    </div>
  );
};

export default AuthenticationSection;
